import { Store } from 'lucide-react'
import { useAuth } from '@/auth/AuthContext'
import { Badge } from '@/components/ui/badge'
import { Card } from '@/components/ui/card'
import { VendorProfileLink } from '@/components/VendorProfileLink'
import { AskForQuoteButton } from '@/components/quotes/QuoteRequestDialog'
import { VENDOR_SERVICE_LABELS } from '@lib/enums/vendor-service.enum'
import { offeringPriceLabel } from '@lib/helpers/vendor-offering'
import type { DirectoryPriceDto, DirectoryVendorDto } from '@lib/dto/vendor-directory.dto'

/** "From $85.00 per guest", or "Price on request". */
function priceLine(price: DirectoryPriceDto): string {
  return price.kind === 'on_request' ? offeringPriceLabel(null) : offeringPriceLabel({ cents: price.cents, from: price.from, unit: price.unit })
}

interface Props {
  vendor: DirectoryVendorDto
  /** The event it is browsed from, which a quote would be for. */
  eventId: string
  venueName: string
  /** On the event page: no price line, which only means something within a category. */
  compact?: boolean
}

/**
 * One of the venue's vendors as a couple or planner browses them
 * (docs/vendor-growth.spec.md, "Couples find vendors"): its logo, its name,
 * which opens its profile, what it does here, the venue's recommendation, and
 * its price in the category being browsed.
 */
export function DirectoryVendorCard({ vendor, eventId, venueName, compact = false }: Props) {
  const { user } = useAuth()
  // A planner browsing finds its own company here too, and doesn't ask itself.
  const canAsk = user?.vendorId !== vendor.vendorId
  return (
    <Card asChild className="flex gap-3 p-4">
      <li>
        {vendor.logoUrl ? (
          <img src={vendor.logoUrl} alt="" className="h-12 w-12 shrink-0 rounded-md border border-line bg-surface object-contain p-1" />
        ) : (
          <span className="grid h-12 w-12 shrink-0 place-items-center rounded-md bg-surface-sunken">
            <Store className="h-5 w-5 text-ink-muted" aria-hidden />
          </span>
        )}
        <div className="min-w-0 space-y-1">
          <p className="m-0 font-medium">
            <VendorProfileLink vendorId={vendor.vendorId} name={vendor.name} context={{ event: { id: eventId, canAskForQuote: canAsk } }} />
          </p>
          <p className="m-0 text-xs text-ink-muted">{vendor.services.map((s) => VENDOR_SERVICE_LABELS[s]).join(' · ')}</p>
          {vendor.recommendedByVenue ? <Badge tone="sage">Recommended by {venueName}</Badge> : null}
          {!compact && vendor.price !== null ? <p className="m-0 text-sm text-ink">{priceLine(vendor.price)}</p> : null}
          {canAsk ? (
            <div className="pt-1">
              <AskForQuoteButton eventId={eventId} vendor={{ id: vendor.vendorId, name: vendor.name }} />
            </div>
          ) : null}
        </div>
      </li>
    </Card>
  )
}
